import { format, endOfWeek, eachWeekOfInterval, eachDayOfInterval, eachMonthOfInterval, endOfMonth, getHours } from 'date-fns';
import { formatWeekLabel } from '../../i18n/date-locale';

export interface TimeSeriesData {
  totalValue: number;
  timeSeries: Date;
}

export interface GroupedChartData {
  label: string;
  dateRange: string;
  [key: string]: any;
}

export class TimeSeriesGroupingHelper {
  private static readonly hourInterval = 6;
  private static readonly hourlyLabels = [
    '00:00-05:59', '06:00-11:59', '12:00-17:59', '18:00-23:59'
  ];

  /**
   * Calculates the actual start and end dates for a time series week,
   * ensuring the week boundaries don't exceed the requested date range
   * @param timeSeriesWeekStart The start of the time series week (Monday)
   * @param dataRangeStart The earliest date in the data range
   * @param dataRangeEnd The latest date in the data range
   * @returns Object containing the clamped week boundaries for time series calculations
   */
  private static getWeekBounds(
    timeSeriesWeekStart: Date,
    dataRangeStart: Date,
    dataRangeEnd: Date
  ) {
    // Calculate the natural end of the time series week (Sunday)
    const timeSeriesWeekEnd = endOfWeek(timeSeriesWeekStart, { weekStartsOn: 1 });

    // Ensure week start doesn't go before the data range
    const actualWeekStart = timeSeriesWeekStart < dataRangeStart ? dataRangeStart : timeSeriesWeekStart;

    // Ensure week end doesn't go beyond the data range
    const actualWeekEnd = timeSeriesWeekEnd > dataRangeEnd ? dataRangeEnd : timeSeriesWeekEnd;

    return {
      weekStart: actualWeekStart,
      weekEnd: actualWeekEnd
    };
  }

  /**
   * Groups time series data into hourly buckets (6-hour intervals)
   * @param datasets Array of datasets with time series data
   * @param useIndividualHours If true, shows all 24 hours instead of 6-hour groups
   * @returns Grouped chart data with date ranges for tooltips
   */
  static groupByHours<T extends TimeSeriesData>(
    datasets: { data: T[], label: string }[],
    useIndividualHours: boolean = false
  ): GroupedChartData[] {
    if (!datasets?.length) return [];

    if (useIndividualHours) {
      // Generate labels for all 24 hours (00:00, 01:00, ..., 23:00)
      const allHourLabels = Array.from({ length: 24 }, (_, i) =>
        `${i.toString().padStart(2, '0')}:00`
      );

      const result = this.aggregateData(
        allHourLabels,
        datasets,
        (timeSeries) => getHours(new Date(timeSeries))
      );

      return result.map(item => ({
        ...item,
        dateRange: item.label
      }));
    }

    // Default: 6-hour intervals
    const result = this.aggregateData(
      this.hourlyLabels,
      datasets,
      (timeSeries) => Math.floor(getHours(new Date(timeSeries)) / this.hourInterval)
    );

    return result.map(item => ({
      ...item,
      dateRange: item.label
    }));
  }

  /**
   * Groups time series data into daily buckets
   * @param datasets Array of datasets with time series data
   * @param startDate Start date of the period
   * @param endDate End date of the period
   * @param maxDays Optional maximum number of days to display (defaults to no limit)
   * @returns Grouped chart data with date ranges for tooltips
   */
  static groupByDays<T extends TimeSeriesData>(
    datasets: { data: T[], label: string }[],
    startDate: Date,
    endDate: Date,
    maxDays?: number
  ): GroupedChartData[] {
    if (!datasets?.length || !startDate || !endDate || startDate > endDate) {
      return [];
    }

    const allDayDates = eachDayOfInterval({ start: startDate, end: endDate });
    const dayDates = maxDays ? allDayDates.slice(0, maxDays) : allDayDates;

    // Use day names (Mon, Tue, Wed, etc.) for 7 days or less, otherwise use date
    const labels = dayDates.length <= 7
      ? dayDates.map(date => format(date, 'EEE'))
      : dayDates.map(date => format(date, 'd MMM'));

    const result = this.aggregateData(
      labels,
      datasets,
      (timeSeries) => {
        const timeSeriesDate = new Date(timeSeries);
        const dayIndex = dayDates.findIndex(dayDate =>
          format(dayDate, 'yyyy-MM-dd') === format(timeSeriesDate, 'yyyy-MM-dd')
        );
        return dayIndex;
      }
    );

    return result.map((item, i) => ({
      ...item,
      dateRange: format(dayDates[i], 'dd MMM, yyyy')
    }));
  }

  /**
   * Groups time series data into weekly buckets
   * @param datasets Array of datasets with time series data
   * @param startDate Start date of the period
   * @param endDate End date of the period
   * @returns Grouped chart data with date ranges for tooltips
   */
  static groupByWeeks<T extends TimeSeriesData>(
    datasets: { data: T[], label: string }[],
    startDate: Date,
    endDate: Date
  ): GroupedChartData[] {
    if (!datasets?.length || !startDate || !endDate || startDate > endDate) {
      return [];
    }

    // Generate all week starts within the interval
    const weekStarts = eachWeekOfInterval(
      { start: startDate, end: endDate },
      { weekStartsOn: 1 } // Monday
    );

    // Create simple week labels for chart display (translated by the language layer)
    const labels = weekStarts.map((_, i) => formatWeekLabel(i + 1));

    // Create time series week date ranges for chart tooltips
    const timeSeriesWeekDateRanges = weekStarts.map(timeSeriesWeekStart => {
      const { weekStart: actualStart, weekEnd: actualEnd } = this.getWeekBounds(timeSeriesWeekStart, startDate, endDate);
      return `${format(actualStart, 'dd MMM')} - ${format(actualEnd, 'dd MMM')}`;
    });

    // Pre-filter dataset to range
    const filteredDatasets = datasets.map(dataset => ({
      ...dataset,
      data: dataset.data.filter(item => {
        const d = new Date(item.timeSeries);
        return d >= startDate && d <= endDate;
      })
    }));

    // Aggregate values into weekly buckets
    const result = this.aggregateData(
      labels,
      filteredDatasets,
      (timeSeries) => {
        const dataDate = new Date(timeSeries);
        return weekStarts.findIndex(timeSeriesWeekStart => {
          const { weekStart: actualStart, weekEnd: actualEnd } = this.getWeekBounds(timeSeriesWeekStart, startDate, endDate);
          return dataDate >= actualStart && dataDate <= actualEnd;
        });
      }
    );

    // Add time series week date ranges for chart tooltips
    return result.map((item, i) => ({
      ...item,
      dateRange: timeSeriesWeekDateRanges[i]
    }));
  }

  /**
   * Groups time series data into monthly buckets
   * @param datasets Array of datasets with time series data
   * @param startDate Start date of the period
   * @param endDate End date of the period
   * @returns Grouped chart data with date ranges for tooltips
   */
  static groupByMonths<T extends TimeSeriesData>(
    datasets: { data: T[], label: string }[],
    startDate: Date,
    endDate: Date
  ): GroupedChartData[] {
    if (!datasets?.length || !startDate || !endDate || startDate > endDate) {
      return [];
    }

    // Generate all month starts within the interval
    const monthStarts = eachMonthOfInterval({ start: startDate, end: endDate });

    // Create month abbreviation labels for chart display (e.g., "Jan", "Feb", "Mar")
    const labels = monthStarts.map(monthStart => format(monthStart, 'MMM'));

    // Create month date ranges for chart tooltips (e.g., "January 2025")
    const monthDateRanges = monthStarts.map(monthStart =>
      format(monthStart, 'MMMM yyyy')
    );

    // Pre-filter dataset to range
    const filteredDatasets = datasets.map(dataset => ({
      ...dataset,
      data: dataset.data.filter(item => {
        const d = new Date(item.timeSeries);
        return d >= startDate && d <= endDate;
      })
    }));

    // Aggregate values into monthly buckets
    const result = this.aggregateData(
      labels,
      filteredDatasets,
      (timeSeries) => {
        const dataDate = new Date(timeSeries);
        return monthStarts.findIndex(monthStart => {
          const monthEnd = endOfMonth(monthStart);
          const actualStart = monthStart < startDate ? startDate : monthStart;
          const actualEnd = monthEnd > endDate ? endDate : monthEnd;
          return dataDate >= actualStart && dataDate <= actualEnd;
        });
      }
    );

    // Add month date ranges for chart tooltips
    return result.map((item, i) => ({
      ...item,
      dateRange: monthDateRanges[i]
    }));
  }

  /**
   * Core aggregation logic that groups data by time periods
   */
  private static aggregateData<T extends TimeSeriesData>(
    labels: string[],
    datasets: { data: T[], label: string }[],
    indexFn: (timeSeries: Date) => number
  ): GroupedChartData[] {
    // Initialize result array
    const result: GroupedChartData[] = labels.map(label => ({
      label,
      dateRange: ''
    }));

    // Process each dataset
    datasets.forEach(({ data, label: datasetLabel }) => {
      // Initialize all values to 0
      result.forEach(row => { row[datasetLabel] = 0; });

      // Aggregate values efficiently - calculate index once per item
      data.forEach(item => {
        const index = indexFn(item.timeSeries);
        if (index >= 0 && index < result.length) {
          result[index][datasetLabel] = (result[index][datasetLabel] as number) + (item.totalValue || 0);
        }
      });
    });

    return result;
  }
}